import { useCallback } from 'react';
import { createBridgeChannel } from './createBridgeChannel';
import { RECONNECT_SUCCEEDED_CHANNEL, } from '../types/reconnectChannel';
/**
 * Cross-app channel announcing that the shell finished reconnecting a
 * calendar/account integration, built on the generic {@link createBridgeChannel}
 * primitive over the shell-owned {@link ShellComposerBridge}. See
 * `types/reconnectChannel.ts` for the payload contract and the
 * backwards-compatibility policy.
 *
 * This uses the bridge rather than `window` events for the same reason
 * `eventDraftChannel` does: each micro-frontend bundles its own copy of
 * `@robinpowered/dashboard-apps-common`, so a module-level singleton here
 * would be a different instance per MFE bundle and couldn't carry messages
 * between apps. The shell-provided bridge is the one object every bundle
 * shares (see `shellComposerBridge.ts`).
 */
var channel = createBridgeChannel(RECONNECT_SUCCEEDED_CHANNEL);
/**
 * Announce on the cross-app channel that a calendar/account reconnect
 * succeeded. Prefer `useEmitReconnectSucceeded` in React components; this
 * bridge-taking form is for tests holding an explicit bridge.
 */
export function emitReconnectSucceeded(bridge, accountType) {
    channel.emit(bridge, { accountType: accountType });
}
/**
 * Subscribe to reconnect-succeeded events. Returns an unsubscribe function.
 * Prefer `useReconnectSucceeded` in React components; this bridge-taking
 * form is for tests holding an explicit bridge.
 */
export function onReconnectSucceeded(bridge, listener) {
    return channel.subscribe(bridge, listener);
}
/**
 * Subscribe to reconnect-succeeded events from inside a React component. The
 * subscription is set up once per bridge instance and torn down on unmount;
 * the latest `listener` closure runs on every event, so callers don't need
 * to memoize it and passing a new closure on every render never causes a
 * re-subscription.
 *
 * @example
 * useReconnectSucceeded(({ accountType }) => {
 *   if (accountType === 'GOOGLE') refetchGoogleHealth();
 * });
 */
export function useReconnectSucceeded(listener) {
    channel.useSubscribe(listener);
}
/**
 * Returns a stable callback that emits a reconnect-succeeded event on the
 * shell bridge. Reads the bridge from context so callers don't thread it
 * manually.
 */
export function useEmitReconnectSucceeded() {
    var emit = channel.useEmit();
    return useCallback(function (accountType) { return emit({ accountType: accountType }); }, [emit]);
}
